/* iter55u — Extracted from AdminHelpDrawer.jsx.
 *
 * `HelpSectionEditor` owns the edit-mode form for a single section:
 * title/summary inputs, dynamic block list (add/remove/reorder), and
 * the Save / Cancel action bar. The parent hands it the working
 * `draft` state plus the `setDraft` setter — no fetch/persist logic
 * lives here (that stays in the drawer's coordinator).
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, Save, Loader2 } from 'lucide-react';
import { BlockEditor } from './AdminHelpBlocks';

const _nextLid = () =>
  `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export function HelpSectionEditor({ draft, setDraft, saving, onSave, onCancel }) {
  if (!draft) return null;
  const updateBlock = (i, next) => {
    const arr = [...draft.blocks];
    arr[i] = { ...next, _lid: draft.blocks[i]._lid };
    setDraft({ ...draft, blocks: arr });
  };
  const removeBlock = (i) =>
    setDraft({ ...draft, blocks: draft.blocks.filter((_, j) => j !== i) });
  const addBlock = () =>
    setDraft({
      ...draft,
      blocks: [
        ...(draft.blocks || []),
        { kind: 'para', text: 'New paragraph.', _lid: _nextLid() },
      ],
    });

  return (
    <div className="space-y-3">
      <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Editing</div>
      <div>
        <Label className="text-[10.5px]">Title</Label>
        <Input
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          className="h-8 text-[12.5px]"
          data-testid="admin-help-edit-title"
        />
      </div>
      <div>
        <Label className="text-[10.5px]">Summary</Label>
        <Input
          value={draft.summary || ''}
          onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
          className="h-8 text-[12.5px]"
          data-testid="admin-help-edit-summary"
        />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-[10.5px]">Blocks</Label>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={addBlock}>
            Add block
          </Button>
        </div>
        {(draft.blocks || []).map((b, i) => (
          <BlockEditor
            key={b._lid || `idx-${i}`}
            block={b}
            onChange={(next) => updateBlock(i, next)}
            onRemove={() => removeBlock(i)}
          />
        ))}
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button size="sm" variant="ghost" onClick={onCancel} className="h-8 gap-1.5">
          <X className="h-3.5 w-3.5" /> Cancel
        </Button>
        <Button
          size="sm"
          onClick={onSave}
          disabled={saving}
          className="h-8 gap-1.5"
          data-testid="admin-help-save"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
        </Button>
      </div>
    </div>
  );
}
